import {
  ORDER_ROUTES,
  PURCHASE_ROUTES,
  orderDetailResponseSchema,
  orderListResponseSchema,
  orderResponseSchema,
  orderStatusResponseSchema,
  purchaseListResponseSchema,
  type Commission,
  type Order,
  type OrderList,
  type OrderListQuery,
  type OrderProofInput,
  type OrderStatus,
  type Purchase,
  type PurchaseList,
  type PurchaseSummary,
  type PurchasesAdminQuery,
  purchaseSummaryResponseSchema,
} from "@nh/shared";
import { authFetch, getAccessToken } from "@/shared/api/session";

/**
 * Órdenes y compras vistas desde el panel: listar, ver el detalle,
 * subir el comprobante de pago y cambiar el estado a mano. El ALTA de
 * órdenes existe en el API pero ninguna pantalla la llama — el checkout
 * será quien cree las de verdad.
 *
 * `baseUrl: ""` porque las rutas del contrato YA traen el prefijo `/api`.
 */
const NO_PREFIX = { baseUrl: "" } as const;

/** La URL con el orden de parámetros FIJO, para el caché y las pruebas. */
function listUrl(base: string, query: OrderListQuery): string {
  const params = new URLSearchParams();
  params.set("page", String(query.page));
  params.set("perPage", String(query.perPage));
  if (query.query) params.set("query", query.query);
  if (query.status) params.set("status", query.status);
  if (query.from) params.set("from", query.from);
  if (query.to) params.set("to", query.to);
  if (query.minTotal !== undefined) params.set("minTotal", String(query.minTotal));
  if (query.maxTotal !== undefined) params.set("maxTotal", String(query.maxTotal));
  return `${base}?${params.toString()}`;
}

export function adminOrdersUrl(query: OrderListQuery): string {
  return listUrl(ORDER_ROUTES.adminList, query);
}

/** Los filtros del dashboard, con orden FIJO para el caché. */
function purchasesParams(query: PurchasesAdminQuery): string {
  const params = new URLSearchParams();
  params.set("page", String(query.page));
  params.set("perPage", String(query.perPage));
  if (query.from) params.set("from", query.from);
  if (query.to) params.set("to", query.to);
  if (query.country) params.set("country", query.country);
  return params.toString();
}

export function adminPurchasesUrl(query: PurchasesAdminQuery): string {
  return `${PURCHASE_ROUTES.adminList}?${purchasesParams(query)}`;
}

export function listAdminOrders(query: OrderListQuery): Promise<OrderList> {
  return authFetch(adminOrdersUrl(query), orderListResponseSchema, NO_PREFIX);
}

export function listAdminPurchases(query: PurchasesAdminQuery): Promise<PurchaseList> {
  return authFetch(adminPurchasesUrl(query), purchaseListResponseSchema, NO_PREFIX);
}

export function getPurchasesSummary(query: PurchasesAdminQuery): Promise<PurchaseSummary> {
  return authFetch(
    `${PURCHASE_ROUTES.adminSummary}?${purchasesParams(query)}`,
    purchaseSummaryResponseSchema,
    NO_PREFIX,
  );
}

/**
 * Descarga el CSV del periodo COMO ARCHIVO: la petición lleva el token
 * a mano (no es JSON, `authFetch` no aplica) y el blob se entrega con un
 * `<a download>` — el nombre lo decide el servidor.
 */
export async function downloadPurchasesCsv(query: PurchasesAdminQuery): Promise<void> {
  const token = getAccessToken();
  const response = await fetch(`${PURCHASE_ROUTES.adminExport}?${purchasesParams(query)}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!response.ok) throw new Error("No se pudo generar el archivo");

  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = `compras_${query.from ?? "inicio"}_${query.to ?? "hoy"}.csv`;
  anchor.click();
  URL.revokeObjectURL(url);
}

export interface OrderDetail {
  order: Order;
  /** Las compras que la conversión creó. Vacío mientras siga pendiente. */
  purchases: Purchase[];
  /** Las comisiones REGISTRADAS por esta orden. Vacío = no generó. */
  commissions: (Commission & { userName: string })[];
}

/** El detalle trae el comprobante entero (`dataUrl`) y las compras. */
export function getAdminOrder(id: string): Promise<OrderDetail> {
  return authFetch(ORDER_ROUTES.adminOne(id), orderDetailResponseSchema, NO_PREFIX);
}

export async function uploadOrderProof(
  id: string,
  input: OrderProofInput,
): Promise<Order> {
  const data = await authFetch(ORDER_ROUTES.adminProof(id), orderResponseSchema, {
    ...NO_PREFIX,
    method: "POST",
    body: input,
  });
  return data.order;
}

/** Un link de pago de Stripe nuevo para una orden pendiente (22-oct-2026). */
export async function createOrderStripeLink(id: string): Promise<Order> {
  const data = await authFetch(ORDER_ROUTES.adminStripeLink(id), orderResponseSchema, {
    ...NO_PREFIX,
    method: "POST",
  });
  return data.order;
}

export interface OrderStatusResult {
  order: Order;
  createdPurchases: Purchase[];
}

/**
 * El cambio manual de estado. La respuesta trae las compras que ese
 * cambio creó (vacío si no convirtió): la pantalla las anuncia con sus
 * folios.
 */
export function updateOrderStatus(
  id: string,
  status: OrderStatus,
): Promise<OrderStatusResult> {
  return authFetch(ORDER_ROUTES.adminStatus(id), orderStatusResponseSchema, {
    ...NO_PREFIX,
    method: "PATCH",
    body: { status },
  });
}
